/**
 * Application configuration
 */

// Helper function to get API URL
const getApiUrl = () => {
  // First try environment variable
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Browser: same origin + CRA "proxy" (package.json) forwards /api to the backend.
  // Important: preview URLs must stay on this origin so <iframe>/<embed> is not blocked
  // by X-Frame-Options when the UI runs on :3001 and the API on :3000.
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api`;
  }
  
  // Fallback for SSR
  return 'http://localhost:3000/api';
};

// Helper function to get WebSocket URL
const getWsUrl = () => {
  if (process.env.REACT_APP_WS_URL) {
    return process.env.REACT_APP_WS_URL;
  }
  // Realtime is hosted by the API, which may differ from the static UI origin.
  if (process.env.REACT_APP_API_URL) {
    const api = new URL(process.env.REACT_APP_API_URL);
    return `${api.protocol === 'https:' ? 'wss:' : 'ws:'}//${api.host}`;
  }
  // Same-origin development fallback
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:';
    // If running React dev server on 3001, connect to backend WS on port 3000
    if (port === '3001') {
      return `${wsProtocol}//${hostname}:3000`;
    }
    // Otherwise use same origin
    return `${wsProtocol}//${hostname}${port ? `:${port}` : ''}`;
  }
  // Fallback
  return 'ws://localhost:3000';
};

const getDriveApiUrl = () => {
  if (process.env.REACT_APP_DRIVE_API_URL) {
    return process.env.REACT_APP_DRIVE_API_URL.replace(/\/$/, '');
  }
  // Derive host origin from product API URL when possible (strip /api)
  if (process.env.REACT_APP_API_URL) {
    try {
      const u = new URL(process.env.REACT_APP_API_URL);
      return u.origin;
    } catch {
      /* fall through */
    }
  }
  return 'https://api.drive.cynayd.com';
};

const config = {
  // API server
  server: {
    baseUrl: getApiUrl(),
    wsUrl: getWsUrl(),
    /** Auth BFF host (exchange/session) — not necessarily the same path as product /api */
    driveApiUrl: getDriveApiUrl(),
    oneOrigin: (process.env.REACT_APP_ONE_ORIGIN || 'https://one.cynayd.com').replace(
      /\/$/,
      ''
    )
  },
  
  // API endpoints
  api: {
    base: '/api',
    files: '/files',
    folders: '/folders',
    activities: '/activities',
    users: '/users',
    auth: '/auth',
    sso: '/sso',
    shares: '/shares',
    search: '/search',
    websocket: '/ws',
    health: '/health',
    dashboard: '/dashboard',
    storage: '/storage'
  },

  sso: {
    appSlug: process.env.REACT_APP_SSO_APP_SLUG || 'cynayd-drive'
  },

  docs: {
    baseUrl: process.env.REACT_APP_DOCS_URL || 'https://docs.cynayd.com'
  },

  grid: {
    baseUrl: process.env.REACT_APP_GRID_URL || 'https://grid.cynayd.com'
  },
  
  // File upload limits
  upload: {
    maxFileSize: 1024 * 1024 * 1024 * 5, // 5GB
    maxTotalSize: 1024 * 1024 * 1024 * 10, // 10GB
    /**
     * Axios timeout (ms) for multipart POST /files/upload only.
     * 0 = no timeout (recommended for large/slow uploads). Override with REACT_APP_UPLOAD_REQUEST_TIMEOUT_MS.
     */
    requestTimeoutMs: (() => {
      const raw = process.env.REACT_APP_UPLOAD_REQUEST_TIMEOUT_MS;
      if (raw === undefined || raw === '') return 0;
      const n = parseInt(raw, 10);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    })(),
    allowedTypes: [
      'image/*', 
      'application/pdf', 
      'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'application/zip',
      'application/x-rar-compressed',
      'audio/*',
      'video/*'
    ]
  },
  
  // UI configuration
  ui: {
    thumbnailSize: {
      width: 250,
      height: 250
    },
    refreshInterval: 300000, // 5 minutes
    notification: {
      defaultDuration: 5000 // 5 seconds
    },
    // Error handling configuration
    errors: {
      // Whether to show notifications for API errors
      showErrorNotifications: true,
      // Whether to retry failed requests automatically
      retryFailedRequests: true,
      // Maximum number of retries for failed requests
      maxRetries: 2
    }
  },
  
  // React Router future flags
  router: {
    // Opt-in to React Router v7 flags
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  },
  
  // Development options
  development: {
    // Whether to use mock data for unavailable endpoints
    useMockData: true,
    // Whether to show console warnings for API errors
    showApiWarnings: true,
    // Whether to simulate delay for API responses (in ms, 0 to disable)
    simulateApiDelay: 0
  }
};

export default config; 